// The waiting room of a game among friends (created from the Lobby, joined with its code or invite
// link, or a rematch). The same table as the online room (WaitingTable), plus what friends need: the
// code to type in the Lobby (top bar, tap to copy; no invite button, owner's choice), the host's crown,
// and the host's bot stepper and « Lancer » button. The top bar shows the room's state, not its name
// (« Partie de <host> », owner's choice). A rematch room keeps its free seats for the players of the
// previous game: their names wait on those seats.
import React, { useState } from "react";
import { motion } from "framer-motion";
import { Bot, Check, Copy, Minus, Plus, RotateCcw } from "lucide-react";
import { toast } from "../../lib/notify";
import { t, useLang } from "../../lib/i18n";
import {
  LaunchButton, LeaveButton, RulesChips, TableColorSection, Tips, WaitingTable, WaitingTitle, useSeatChime,
} from "./WaitingTable";

export default function FriendsWaitingRoom({ room, roomId, user, onLeave, onStart, onBots }) {
  useLang();
  const [codeCopied, setCodeCopied] = useState(false);
  const [starting, setStarting] = useState(false);
  useSeatChime(room.members, room.num_players, user.user_id);

  const total = room.num_players;
  const seated = Math.min(room.members.length, total);
  const full = seated >= total;
  const slotsEmpty = total - seated;
  const isHost = room.host_user_id === user.user_id;
  const botCount = room.members.filter((m) => m.is_bot).length;
  const maxBots = total - (room.members.length - botCount);
  // Rematch room: the free seats are kept for players of the previous game.
  const isRematch = !!room.rematch_of;
  const seatedHere = (p) => room.members.some((m) => m.user_id === p.user_id);
  // Then the players invited from their card (backend/social.py): « Invité », or « A refusé » a moment.
  const invited = (room.invited || []).filter((p) => !seatedHere(p)).map((p) => ({
    ...p,
    declined: p.status === "declined",
    label: p.status === "declined" ? t("invite.seatDeclined") : t("invite.seatPending"),
  }));
  const awaited = [...(room.reserved_players || []).filter((p) => !seatedHere(p)), ...invited];
  // Short code a friend types in the Lobby ("Rejoindre avec un code").
  const code = room.code || roomId.replace(/^room_/, "").slice(0, 6).toUpperCase();

  const copyCode = () => {
    navigator.clipboard.writeText(code);
    setCodeCopied(true);
    toast.success(t("room.codeCopied"));
    setTimeout(() => setCodeCopied(false), 2000);
  };

  const start = () => {
    setStarting(true);
    Promise.resolve(onStart()).finally(() => setTimeout(() => setStarting(false), 1500));
  };

  // The only human, with bots seated: the game will not count for the rank (backend/ranking.py).
  const aloneWithBots = botCount > 0 && room.members.length - botCount === 1;

  const subline = isRematch && !full
    ? t("room.rematchNote")
    : aloneWithBots
    ? t("waitingTable.noRank")
    : !full
    ? isHost ? t("room.shareHint") : t("waitingTable.missing", { n: slotsEmpty })
    : isHost
    ? t("waitingTable.hostStarts")
    : t("waitingTable.waitHost");

  // A game is played by 2, 3 or 4: the host may start once 2 are seated, without the free seats.
  const cta = isHost && starting
    ? { busy: true, live: true, label: t("waitingTable.starting") }
    : isHost && full
    ? { onClick: start, live: true, label: t("room.start") }
    : isHost && seated >= 2
    ? { onClick: start, live: true, label: t("room.startWith", { n: seated }) }
    : !full
    ? { busy: true, label: t("room.waitingCount", { a: seated, b: total }) }
    : { busy: true, live: true, label: t("room.waitingLaunch") };

  return (
    <div className="w-full max-w-lg flex flex-col gap-2">
      {/* ── The table ─────────────────────────────────────────────── */}
      <motion.div
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3 }}
        className="rami-glass rounded-2xl border border-white/[0.08] p-3 sm:p-3.5 flex flex-col gap-2.5"
        data-testid="friends-waiting-room"
      >
        <div className="flex items-center justify-between gap-2">
          <LeaveButton onClick={onLeave} />

          {/* The room's state, which changes once the table is full. */}
          <WaitingTitle
            title={full ? t("waitingTable.full") : t("room.waitingTitle")}
            searching={!full}
            className="min-w-0 flex-1 text-center text-[17px] leading-tight"
          />

          {isRematch ? (
            <span
              data-testid="rematch-badge"
              className="flex shrink-0 items-center gap-1.5 px-3 py-1.5 rounded-full text-[11px] font-semibold border bg-emerald-500/10 border-emerald-500/30 text-emerald-300"
            >
              <RotateCcw className="h-3 w-3" /> {t("common.rematchBadge")}
            </span>
          ) : slotsEmpty > 0 ? (
            // Code de la partie — typed in the Lobby ("Rejoindre avec un code"); tap to copy.
            <button
              onClick={copyCode}
              data-testid="room-code"
              title={t("room.codeLabel")}
              className="flex shrink-0 items-center gap-1.5 rounded-lg border border-white/10 bg-slate-900/50 px-2.5 py-1 hover:border-amber-400/40 transition-colors"
            >
              <span className="text-[11px] text-slate-400">{t("room.codeShort")}</span>
              <span dir="ltr" lang="en" className="font-mono text-[13px] font-bold tracking-[0.15em] text-amber-300">{code}</span>
              {codeCopied
                ? <Check className="h-3.5 w-3.5 text-emerald-400" />
                : <Copy className="h-3.5 w-3.5 text-slate-500" />}
            </button>
          ) : <div className="w-9 shrink-0" />}
        </div>

        <RulesChips room={room} />

        <p className="text-center text-[12px] text-slate-400">{subline}</p>

        <WaitingTable
          room={room}
          user={user}
          freeLabel={t("room.waitingSlot")}
          showHost
          awaited={awaited}
          starting={starting}
        />

        {/* Host: bots on the free seats. Stepper: one solid capsule, − / count / +. */}
        {isHost && (
          <div className="flex items-center justify-between gap-2 rounded-xl border border-white/[0.06] bg-white/[0.03] px-3 py-1">
            <span className="flex items-center gap-1.5 text-[12px] font-semibold text-slate-100">
              <Bot className="h-3.5 w-3.5 text-emerald-300" /> {t("room.addBots")}
            </span>
            <div className="inline-flex items-center h-7 p-0.5 rounded-full bg-white/[0.08] border border-white/[0.14] shadow-[inset_0_1px_0_rgba(255,255,255,0.06)]">
              <button
                data-testid="remove-bot-btn"
                disabled={botCount === 0}
                onClick={() => onBots(botCount - 1)}
                className="h-6 w-6 rounded-full flex items-center justify-center text-slate-100 hover:bg-white/10 active:scale-90 transition disabled:text-slate-600 disabled:hover:bg-transparent disabled:active:scale-100 disabled:cursor-not-allowed"
              >
                <Minus className="h-3.5 w-3.5" strokeWidth={2.5} />
              </button>
              <span className="min-w-[2.25rem] px-1 flex items-center justify-center text-[14px] font-bold text-white tabular-nums">
                {botCount}
              </span>
              <button
                data-testid="add-bot-btn"
                disabled={botCount >= maxBots}
                onClick={() => onBots(botCount + 1)}
                className="h-6 w-6 rounded-full flex items-center justify-center bg-emerald-400 text-emerald-950 shadow-[0_2px_8px_rgba(52,211,153,0.35)] hover:bg-emerald-300 active:scale-90 transition disabled:bg-white/[0.06] disabled:text-slate-600 disabled:shadow-none disabled:active:scale-100 disabled:cursor-not-allowed"
              >
                <Plus className="h-3.5 w-3.5" strokeWidth={2.75} />
              </button>
            </div>
          </div>
        )}

        <LaunchButton {...cta} />
      </motion.div>

      {/* ── While waiting: a tip, the felt ────────────────────────── */}
      <motion.div
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3, delay: 0.08 }}
        className="rami-glass rounded-2xl border border-white/[0.08] p-3 sm:p-3.5 flex flex-col gap-3"
      >
        <Tips room={room} />
        <TableColorSection />
      </motion.div>
    </div>
  );
}
